/**
 * From session + pack + UI state to what the screen shows: the screen definition, the actions the
 * backend allows, the active mode, the resolved layout. Pure functions, so every rule is testable.
 */
import type { AccessibilityMode, Session } from '../core/wire';
import type { Translate } from '../i18n/translator';
import type { Appearance } from '../core/appearance';
import { findNode, nameOf, parentOf, type LayoutChange } from '../layout/changes';
import type { Preset } from '../layout/model';
import { resolveLayout, type ResolvedLayout } from '../layout/resolve';
import { MODES, type ModeDefinition } from '../modes/modes';
import type { ActionDef, DomainPack, KioskPack, ScreenContext, ScreenDef } from './contract';

const NOTHING_TO_SHOW: ScreenDef = { view: () => null };

/** The screen the spec describes for this state; one it does not: nothing to show, and `known` false (dev mode says so). */
export function screenOf(pack: Pick<KioskPack, 'screens'>, state: string): { def: ScreenDef; known: boolean } {
  const def = pack.screens[state];
  return def ? { def, known: true } : { def: NOTHING_TO_SHOW, known: false };
}

export function screenContext(session: Session, t: Translate, draft: Record<string, unknown>): ScreenContext {
  const allowed = new Set(session.valid_actions);
  return { session, language: session.language, t, draft, can: (action) => allowed.has(action) };
}

/** The actions of a screen that the backend allows right now. */
export function allowedActions(def: ScreenDef, ctx: ScreenContext): ActionDef[] {
  return (def.actions?.(ctx) ?? []).filter((a) => {
    const gate = a.gate === undefined ? a.press?.button : a.gate;
    return gate === null || gate === undefined || ctx.can(gate);
  });
}

/** The mode in force: one chosen on this screen wins, then the backend's announcement, then the session field. */
export function activeModeId(
  sessionMode: AccessibilityMode | null,
  override: AccessibilityMode | null,
  announced: AccessibilityMode | null,
): AccessibilityMode {
  return override ?? announced ?? sessionMode ?? 'none';
}

export function activeMode(id: AccessibilityMode): ModeDefinition {
  return MODES[id];
}

export interface LayoutInputs {
  presets: ReadonlyMap<string, Preset>;
  presetId: string;
  draftPreset: Preset | null;
  pack: DomainPack | null;
  screen: { name: string; def: ScreenDef } | null;
  mode: ModeDefinition;
  knownModules: ReadonlySet<string>;
  /** The face chosen in Settings ('auto': the layout's own, which may be none). */
  face?: Appearance['face'];
}

/** The modules a face shows in: the face itself, or the window the scene's avatar shows through. */
const FACE_MODULES = ['avatar', 'avatar-window'];
/** The height the face gets when it is added (the Essenziale preset's avatar). */
const ADDED_FACE_PX = 240;

/**
 * A face chosen in Settings is always seen: a layout with no place for one (the text preset) gets the avatar module
 * above the conversation, in the split that holds the chat. 'auto' leaves the layout as it is.
 */
export function faceChanges(preset: Preset, face: Appearance['face'] = 'auto'): LayoutChange[] {
  if (face === 'auto' || FACE_MODULES.some((m) => findNode(preset.root, m))) return [];
  const chat = parentOf(preset.root, 'chat');
  return [{ op: 'add', module: 'avatar', into: chat ? nameOf(chat.parent) : preset.root.id, index: chat?.index ?? 0, size: { px: ADDED_FACE_PX } }];
}

export function layoutFor(inputs: LayoutInputs): ResolvedLayout & { missingPreset: string | null } {
  const { presets, presetId, draftPreset, pack, screen, mode, knownModules, face } = inputs;
  const wanted = draftPreset?.id ?? presetId;
  const base = draftPreset ?? presets.get(presetId) ?? presets.get(pack?.defaultPreset ?? 'kiosk') ?? [...presets.values()][0];
  if (!base) throw new Error('no preset available');
  const layers = [
    { source: `domain:${pack?.id ?? 'none'}`, changes: pack?.layout ?? [] },
    { source: `screen:${screen?.name ?? 'none'}`, changes: screen?.def.layout ?? [] },
    { source: `settings:face`, changes: faceChanges(base, face) },
    { source: `mode:${mode.id}`, changes: mode.layout },
  ].filter((l) => l.changes.length > 0);
  const resolved = resolveLayout(base, layers, mode.constraints, knownModules);
  return { ...resolved, missingPreset: base.id === wanted ? null : wanted };
}
